// Finishes uploaded for a build, browsed after the session: view, add, download, delete.
// Applying a finish is Unreal-side only — nothing here touches a running session.
// Backed by the file server's /list-finishes, /upload-finish, /download-finish, /finish.
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { useIsAdmin } from "../admin";
import downloadIcon from "../../assets/icons/download.png";
import "./FinishesPanel.css";

// Same-origin, never the EC2 host directly: that host is HTTP-only and every
// deployment is HTTPS, so a direct fetch is blocked as mixed content. /api/* is
// rewritten to it server-side — by vercel.json in production, src/setupProxy.js
// under `npm start`. Both must list a route for it to exist; adding a call here
// means adding it in both places.
const SERVER = "/api";

// Unreal files a finish with no specific node under this bucket, and the server
// maps fullNodeName === objectCategory to it. Our uploads use the same rule.
const CUSTOM = "_Custom";

// What the file server's /upload-finish accepts (its `allowedMimes`). Checked here
// too so a large texture isn't uploaded over the wire just to come back 400 — and
// so the message names the format instead of "upload failed".
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png"];

/** Null when the file is fine to send, otherwise the message to show the user. */
export function formatError(file) {
  if (!file || ACCEPTED_TYPES.includes(file.type)) return null;
  const kind = file.type || `“${file.name.split(".").pop()}”`;
  return `${file.name} is a ${kind} file. Finishes must be JPG or PNG — convert it and try again.`;
}

// Named sets (Leather, Carpets) lead; the catch-all bucket sits last.
const byName = (a, b) =>
  a === CUSTOM ? 1 : b === CUSTOM ? -1 : a.localeCompare(b);

/**
 * The S3 layout Unreal writes, as two levels of UI:
 * `<objectCategory>/<fullNodeName>/<file>` — e.g. Chair/Leather, Floor/Carpets,
 * with `_Custom` as the node for a finish uploaded against no particular set.
 * Newest first within each set.
 */
export function groupFinishes(finishes = []) {
  const categories = new Map();

  for (const f of finishes) {
    const category = f.objectCategory || CUSTOM;
    const node = f.fullNodeName || CUSTOM;

    if (!categories.has(category)) categories.set(category, new Map());
    const nodes = categories.get(category);
    if (!nodes.has(node)) nodes.set(node, []);
    nodes.get(node).push(f);
  }

  return [...categories.entries()].sort(([a], [b]) => byName(a, b)).map(([category, nodes]) => ({
    category,
    count: [...nodes.values()].reduce((n, items) => n + items.length, 0),
    nodes: [...nodes.entries()]
      .sort(([a], [b]) => byName(a, b))
      .map(([node, items]) => ({
        node,
        items: items.sort((a, b) =>
          String(b.lastModified || "").localeCompare(String(a.lastModified || ""))
        ),
      })),
  }));
}

function prettySize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// The file input is the button — a label wrapping a hidden input, so each
// container carries its own destination with no picker and no shared state.
//
// MUST stay at module scope. Declared inside FinishesPanel it would be a new
// component type on every render, so React would destroy and rebuild this
// <input> each time the panel refreshed. Picking a finish is slow — the OS file
// dialog stays open for seconds, across several background polls — and the
// dialog is bound to the input DOM node. Swap that node out and the change event
// fires on a detached element, never reaches React's root listener, and the
// upload silently never happens. Props may change freely; only the type must not.
function AddButton({ busy, label, onPick }) {
  return (
    <label className={`fx-add ${busy ? "isBusy" : ""}`}>
      {busy || <>+ {label}</>}
      <input
        type="file"
        accept="image/png,image/jpeg"
        hidden
        disabled={!!busy}
        onChange={(e) => {
          onPick(e.target.files[0]);
          e.target.value = ""; // let the same file be picked again after a failure
        }}
      />
    </label>
  );
}

// buildKey is the build's permanent key (builds.key), which is already the S3
// folder name Unreal and ExperiencePage.jsx use. Sent verbatim, never re-normalised.
const FinishesPanel = forwardRef(function FinishesPanel(
  { buildKey, onRefreshingChange },
  ref
) {
  // ponytail: hides the button, nothing more — /finish is an open endpoint and
  // anyone can still curl it. Put auth on the file server if that matters.
  const isAdmin = useIsAdmin();

  const [finishes, setFinishes] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [busy, setBusy] = useState("");

  // background=true for polling, the parent's manual Refresh button, and reloads
  // after an upload/delete — keeps the current grid on screen instead of flashing
  // back to the loading/error state. Errors are logged and swallowed so a flaky
  // poll doesn't blank out a list that was showing fine a moment ago.
  const load = useCallback(
    async ({ background = false } = {}) => {
      if (!buildKey) return;
      if (background) onRefreshingChange?.(true);
      else setState("loading");

      try {
        const res = await fetch(
          `${SERVER}/list-finishes?ProjectName=${encodeURIComponent(buildKey)}`
        );
        const data = await res.json();
        // Keep the previous array when nothing changed, so an idle poll doesn't
        // re-render the grid (and every swatch <img>) every 5 seconds for nothing.
        const next = data.finishes || [];
        setFinishes((prev) =>
          JSON.stringify(prev) === JSON.stringify(next) ? prev : next
        );
        setState("ready");
      } catch (err) {
        console.error("[Finishes] list failed:", err);
        if (!background) setState("error");
      } finally {
        if (background) onRefreshingChange?.(false);
      }
    },
    [buildKey, onRefreshingChange]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Same cadence as the Screenshots tab, so a finish uploaded from Unreal
  // mid-session (or from another tab) shows up without leaving and re-entering.
  //
  // Skipped while the tab is hidden: every tick is a full paginated S3 listing,
  // and a backgrounded tab left open all day was doing that for nobody. The same
  // handler runs on visibilitychange so returning to the tab refreshes at once
  // instead of showing stale finishes for the rest of the interval.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      load({ background: true });
    };

    const interval = setInterval(tick, 5000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load]);

  // Lets ProjectPage's single Refresh button drive this tab too.
  useImperativeHandle(ref, () => ({ refresh: () => load({ background: true }) }), [
    load,
  ]);

  const groups = groupFinishes(finishes);

  const upload = async (file, category, node) => {
    if (!file) return;

    const bad = formatError(file);
    if (bad) {
      window.alert(bad);
      return;
    }

    setBusy("Uploading…");
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("projectName", buildKey);
      form.append("objectCategory", category);
      // Server rule: node === category lands in <category>/_Custom, so pass the
      // category through for an add against the catch-all set.
      form.append("fullNodeName", node === CUSTOM ? category : node);

      const res = await fetch(`${SERVER}/upload-finish`, { method: "POST", body: form });
      if (!res.ok) {
        // The server explains itself ("Only image files (JPG, PNG) are allowed") —
        // show that rather than a status code.
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `upload failed: ${res.status}`);
      }
      await load({ background: true });
    } catch (err) {
      console.error("[Finishes] upload failed:", err);
      window.alert(`Upload failed. ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const remove = async (finish) => {
    if (!window.confirm(`Delete ${finish.filename}? This can't be undone.`)) return;
    setBusy("Deleting…");
    try {
      const res = await fetch(`${SERVER}/finish?key=${encodeURIComponent(finish.key)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`delete failed: ${res.status}`);
      await load({ background: true });
    } catch (err) {
      console.error("[Finishes] delete failed:", err);
      window.alert("Delete failed. See console for details.");
    } finally {
      setBusy("");
    }
  };

  if (!buildKey) return <div className="fx-note">No build selected</div>;
  if (state === "error")
    return <div className="fx-note">Couldn’t load finishes for this project</div>;

  if (state === "loading" && !finishes.length)
    return <div className="fx-note">Loading finishes…</div>;

  if (!finishes.length)
    return (
      <div className="fx-note">
        Nothing uploaded for this project yet. Finishes added here show up in the
        next session.
        <AddButton
          busy={busy}
          label="Add a finish"
          onPick={(file) => upload(file, CUSTOM, CUSTOM)}
        />
      </div>
    );

  return (
    <div className="fx-wrap">
      {groups.map((group) => (
        <section className="fx-cat" key={group.category}>
          <header className="fx-catHead">
            <h3 className="fx-catName">{group.category}</h3>
            <span className="fx-meta">
              {group.count} {group.count === 1 ? "finish" : "finishes"}
            </span>

            {/* Category-level add goes to that category's _Custom set. */}
            <AddButton
              busy={busy}
              label={`Add to ${group.category}`}
              onPick={(file) => upload(file, group.category, CUSTOM)}
            />
          </header>

          {group.nodes.map(({ node: setName, items }) => (
            <div className="fx-set" key={setName}>
              <div className="fx-setHead">
                <span className="fx-setName">{setName}</span>
                <span className="fx-meta">{items.length}</span>
                <AddButton
                  busy={busy}
                  label="Add"
                  onPick={(file) => upload(file, group.category, setName)}
                />
              </div>

              <ul className="fx-grid">
                {items.map((f) => (
                  <li className="fx-tile" key={f.key || f.imageUrl}>
                    <img
                      className="fx-swatch"
                      src={f.imageUrl}
                      alt={f.filename}
                      loading="lazy"
                    />

                    <div className="fx-tileActions">
                      <a
                        className="fx-tileBtn"
                        href={`${SERVER}/download-finish?key=${encodeURIComponent(f.key)}`}
                        title={`Download ${f.filename} (${prettySize(f.size)})`}
                      >
                        <img src={downloadIcon} alt="Download" />
                      </a>

                      {isAdmin && (
                        <button
                          type="button"
                          className="fx-tileBtn fx-delete"
                          onClick={() => remove(f)}
                          disabled={!!busy}
                          title={`Delete ${f.filename}`}
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <span className="fx-tileName" title={f.filename}>
                      {f.filename}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
});

export default FinishesPanel;
